'use client'

import { useTranslation } from 'react-i18next'
import { FaCheck, FaTimes, FaDownload, FaPlay, FaGem } from 'react-icons/fa'
import { HiRefresh } from 'react-icons/hi'
import ClipLoader from 'react-spinners/ClipLoader'
import Portal from './Portal'
import { timeAgo, truncate } from '../helpers/helpers'

const HEADER_ICON =
  'pointer-events-auto h-10 w-10 flex items-center justify-center rounded-full bg-[var(--foreground-bg-color)] hover:bg-[var(--secondary-bg-color)] text-white transition-colors disabled:opacity-50 disabled:pointer-events-none'
const CARD_ICON =
  'flex h-8 w-8 items-center justify-center rounded-full text-white shadow-md transition-colors disabled:opacity-50'

// Presentational: status logic mirrors the legacy video tab; data + handlers live in Create.jsx.
export default function VideoHistoryModal({
  isOpen,
  onClose,
  videoQuery = [],
  videoQueryLoading = false,
  refundLoading = false,
  onRefresh,
  onRefund,
  onSaveAll,
}) {
  const { t } = useTranslation()
  if (!isOpen) return null

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.()
        }}
        // z-[110] dialog tier (same as Modal) clears the create-view's fixed z-30 container, header and drawers.
        className="fixed z-[110] inset-0 bg-black/50 flex justify-center items-center p-4 md:p-12"
      >
        <div className="relative flex w-full max-w-2xl max-h-[85vh] flex-col overflow-hidden rounded-2xl bg-[var(--middle-bg-color)] p-5 text-gray-100 shadow-2xl">
          <header className="flex shrink-0 items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">{t('videoProgress')}</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onRefresh}
                title={t('refresh')}
                aria-label={t('refreshVideoProgress')}
                className={HEADER_ICON}
              >
                <HiRefresh size={18} />
              </button>
              {videoQuery?.length > 0 && (
                <button
                  type="button"
                  onClick={onSaveAll}
                  title={t('saveAllVideos')}
                  aria-label={t('saveAllVideos')}
                  className={HEADER_ICON}
                >
                  <FaDownload size={15} />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                title={t('close')}
                aria-label={t('close')}
                className={HEADER_ICON}
              >
                <FaTimes size={16} />
              </button>
            </div>
          </header>

          <p className="mt-3 shrink-0 text-xs text-gray-400">
            {t('videoProgressDescription')}
          </p>
          <p className="mt-2 flex shrink-0 items-start gap-1.5 rounded-lg bg-[var(--foreground-bg-color)] px-2.5 py-2 text-xs text-gray-300">
            <FaGem
              className="mt-0.5 shrink-0 text-[var(--main-bg-color)]"
              size={11}
            />
            <span>{t('videoRefundNote')}</span>
          </p>

          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            {videoQueryLoading ? (
              <div className="mt-12 flex justify-center">
                <ClipLoader color={'#FFF'} loading size={24} />
              </div>
            ) : videoQuery?.length > 0 ? (
              <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                {videoQuery.map((video, index) => {
                  const isDone =
                    video.status === 'Successful' &&
                    video.final_response?.video?.length > 0
                  const isProcessing =
                    video.status === 'Submitted' ||
                    (!isDone &&
                      video.status !== 'Failed' &&
                      video.status !== 'refund_claimable')
                  const isFailed =
                    !isDone &&
                    video.status === 'Failed' &&
                    video.refund_status === 'Claimed'
                  const isRefundClaimable = video?.refund_claimable === true
                  const progress = Math.round(
                    (video.percentage_complete || 0) * 100,
                  )
                  const videoUrl = video.final_response?.video?.[0]
                  const thumbnail =
                    video.metadata?.base_image_url ??
                    '/assets/models/random.webp'
                  const prompt = video.metadata?.prompt?.trim() || 'Auto'

                  return (
                    <div
                      key={video._id}
                      className="fade-in-out relative overflow-hidden rounded-xl border border-[var(--secondary-bg-color)] bg-[var(--background-bg-color)] shadow-md"
                    >
                      <img
                        src={thumbnail}
                        alt="Base image"
                        className="h-44 w-full object-cover"
                      />

                      <div className="absolute right-2 top-2 flex items-center gap-1.5">
                        {isDone && (
                          <a
                            href={videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View video"
                            aria-label="View video"
                            className={`${CARD_ICON} bg-green-500/80 hover:bg-green-400/80`}
                          >
                            <FaPlay size={11} className="ml-0.5" />
                          </a>
                        )}

                        {isRefundClaimable && (
                          <button
                            type="button"
                            onClick={() => onRefund?.(video._id)}
                            disabled={refundLoading}
                            title="Refund gems"
                            aria-label="Refund gems"
                            className={`${CARD_ICON} bg-orange-500/80 hover:bg-orange-400/80`}
                          >
                            <FaGem size={12} />
                          </button>
                        )}

                        {isFailed && (
                          <span
                            title="Gems refunded"
                            aria-label="Gems refunded"
                            className={`${CARD_ICON} bg-green-500/80`}
                          >
                            <FaCheck size={12} />
                          </span>
                        )}
                      </div>

                      {isProcessing && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/45">
                          <ClipLoader color="#FFF" size={22} />
                          <span className="text-xs font-semibold text-white">
                            {progress}%
                          </span>
                        </div>
                      )}

                      {!isDone &&
                        !isProcessing &&
                        !isFailed &&
                        !isRefundClaimable && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/45 px-2 text-center">
                            <span className="text-xs font-semibold text-red-300">
                              {video.status}
                            </span>
                          </div>
                        )}

                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-2">
                        <p className="truncate text-xs font-medium text-white">
                          {prompt
                            ? truncate(prompt, 18)
                            : `Video #${index + 1}`}
                        </p>
                        <p className="text-[11px] text-gray-300">
                          {timeAgo(video.created_at)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="mt-12 text-center text-sm text-gray-400">
                {t('noVideosYet')}
              </p>
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}
